import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MailPlus } from 'lucide-react'
import { useCreateWorker } from '../index.js'
import type { WorkerProfile } from '../index.js'
import { Modal } from '../../core/index.js'
import { TIER_LABELS } from '../../core/index.js'
import { EMPTY_WORKER_FORM, WorkerFormFields, hasWorkerName, workerFormToInput, type WorkerFormValues } from './WorkerFormFields.js'
import { FORM_INPUT_CLASS, FormError } from '../../core/index.js'
import { Button } from '../../core/index.js'
import { WorkerIcon } from '../../core/index.js'
import { Field } from '../../core/index.js'
import { InvitationHandOffBanner, operatorWorkerLoginApi, workerLoginQueryKey, type InvitationResult, type InviteWorkerInput } from '../../accounts/index.js'

const FORM_ID = 'new-worker-form'

interface NewWorkerModalProps {
  clientId: string | undefined
  facility: { facilityId: string; name: string }
  /** Set when this opens on top of another dialog, naming it. */
  backLabel?: string
  onClose: () => void
  onCreated?: (worker: WorkerProfile) => void
}

/**
 * An operator, or a facility administrator at one of their own
 * facilities, adding one worker to the roster. Every field of the record is on the
 * form; after a save it offers the next worker for the same facility.
 *
 * An email given here invites the worker to the app as soon as they are on
 * the roster (`POST .../workers/{workerId}/login/invitation`), the same
 * invitation the Edit worker dialog's Worker login section sends - the email
 * belongs to the login, never the roster record. A failed invitation leaves
 * the worker on the roster and offers to send it again.
 */
export function NewWorkerModal({ clientId, facility, backLabel, onClose, onCreated }: NewWorkerModalProps) {
  const mutation = useCreateWorker(clientId, facility.facilityId)
  const [form, setForm] = useState<NewWorkerFormValues>(EMPTY_NEW_WORKER_FORM)
  const [created, setCreated] = useState<WorkerProfile | null>(null)
  const [invitation, setInvitation] = useState<InvitationResult | null>(null)
  const queryClient = useQueryClient()
  const invite = useMutation({
    mutationFn: ({ workerId, input }: { workerId: string; input: InviteWorkerInput }) => operatorWorkerLoginApi(clientId ?? '', facility.facilityId, workerId).invite(input),
    onSuccess: (result, { workerId }) => {
      setInvitation(result)
      void queryClient.invalidateQueries({ queryKey: workerLoginQueryKey(clientId ?? '', workerId) })
    },
  })

  function update(changes: Partial<NewWorkerFormValues>): void {
    setForm((current) => ({ ...current, ...changes }))
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (!clientId) {
      return
    }
    const worker = await mutation.mutateAsync(workerFormToInput(form))
    setCreated(worker)
    onCreated?.(worker)
    sendInvitation(worker)
  }

  /** The invitation for the email typed, if any: a text as well where a mobile was entered and asked for. */
  function sendInvitation(worker: WorkerProfile): void {
    const email = form.inviteEmail.trim()
    if (email === '') {
      return
    }
    invite.mutate({ workerId: worker.workerId, input: { email, ...(form.inviteByText && form.mobile.trim() !== '' ? { text: true } : {}) } })
  }

  function addAnother(): void {
    setCreated(null)
    mutation.reset()
    invite.reset()
    setInvitation(null)
    setForm(EMPTY_NEW_WORKER_FORM)
  }

  const canSubmit = Boolean(clientId) && hasWorkerName(form) && !mutation.isPending
  const busy = mutation.isPending || invite.isPending

  return (
    <Modal
      eyebrow="New worker"
      title={created ? 'Worker added' : 'Add a worker'}
      subtitle={
        <span>{facility.name}</span>
      }
      backLabel={backLabel}
      canClose={!busy}
      onClose={onClose}
      footer={
        created ? (
          <div className="flex items-center gap-4">
            <Button icon={WorkerIcon} autoFocus onClick={addAnother} disabled={invite.isPending}>
              Add another worker to {facility.name}
            </Button>
            <button type="button" onClick={onClose} disabled={invite.isPending} className="text-xs font-bold text-marine hover:text-marine-dk disabled:opacity-40">
              Done
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <Button icon={WorkerIcon} type="submit" form={FORM_ID} disabled={!canSubmit}>
              {mutation.isPending ? 'Adding…' : 'Add worker'}
            </Button>
            <button type="button" onClick={onClose} disabled={mutation.isPending} className="text-xs font-bold text-marine hover:text-marine-dk disabled:opacity-40">
              Cancel
            </button>
          </div>
        )
      }
    >
      {created ? (
        <div className="bg-canvas p-6 text-sm text-ink space-y-2">
          <p>
            <span className="font-bold">{created.name}</span> is now on the roster at {facility.name}.
          </p>
          <p className="text-xs text-ink-mid">
            {TIER_LABELS[created.tier]} tier at {created.score} points. The score moves once shift outcomes are reported for them; the tier moves when a period closes.
          </p>
          <InvitationOutcome
            email={form.inviteEmail.trim()}
            texted={form.inviteByText && form.mobile.trim() !== ''}
            pending={invite.isPending}
            error={invite.error}
            result={invitation}
            onRetry={() => sendInvitation(created)}
            onDismissHandOff={() => setInvitation((current) => (current ? { ...current, handOff: null } : current))}
          />
        </div>
      ) : (
        <form id={FORM_ID} onSubmit={(event) => void handleSubmit(event)} className="bg-canvas p-6 space-y-5">
          <WorkerFormFields form={form} onChange={update} inputClass={FORM_INPUT_CLASS} autoFocus />
          <InviteFields form={form} onChange={update} />
          <FormError error={mutation.error} fallback="The worker could not be added." />
        </form>
      )}
    </Modal>
  )
}

/** The New worker form: the worker's record, plus the email their app invitation goes to. */
interface NewWorkerFormValues extends WorkerFormValues {
  /** Blank adds the worker with no login; staff can invite them later from Edit worker. */
  inviteEmail: string
  /** Text the invitation as well, to the mobile entered above. */
  inviteByText: boolean
}

const EMPTY_NEW_WORKER_FORM: NewWorkerFormValues = { ...EMPTY_WORKER_FORM, inviteEmail: '', inviteByText: false }

function InviteFields({ form, onChange }: { form: NewWorkerFormValues; onChange: (changes: Partial<NewWorkerFormValues>) => void }) {
  const hasMobile = form.mobile.trim() !== ''
  return (
    <div className="space-y-2">
      <Field label="Email" htmlFor="new-worker-email" hint="Their username for the worker app. They are emailed a link and a six-digit code, good for seven days, to set their own password. Leave it blank to invite them later from Edit worker.">
        <input id="new-worker-email" type="email" autoComplete="off" className={FORM_INPUT_CLASS} value={form.inviteEmail} onChange={(event) => onChange({ inviteEmail: event.target.value })} />
      </Field>
      {form.inviteEmail.trim() !== '' && (
        <label className={`flex items-center gap-2 text-[11px] ${hasMobile ? 'text-ink' : 'text-ink-mid'}`}>
          <input type="checkbox" checked={form.inviteByText && hasMobile} disabled={!hasMobile} onChange={(event) => onChange({ inviteByText: event.target.checked })} />
          {hasMobile ? 'Also text the invitation to the mobile above' : 'Enter a mobile above to text the invitation as well'}
        </label>
      )}
    </div>
  )
}

interface InvitationOutcomeProps {
  email: string
  texted: boolean
  pending: boolean
  error: unknown
  result: InvitationResult | null
  onRetry: () => void
  onDismissHandOff: () => void
}

/** What became of the app invitation sent with the new worker: sending, sent, failed with a retry, or not asked for. */
function InvitationOutcome({ email, texted, pending, error, result, onRetry, onDismissHandOff }: InvitationOutcomeProps) {
  if (email === '') {
    return <p className="text-xs text-ink-mid">No login yet: invite them to the app from Edit worker when you have their email.</p>
  }
  if (pending) {
    return <p className="text-xs text-ink-mid">Sending the app invitation to {email}…</p>
  }
  if (error) {
    return (
      <div className="space-y-2">
        <FormError error={error} fallback="The app invitation could not be sent." />
        <p className="text-xs text-ink-mid">The worker is on the roster; only the invitation is missing.</p>
        <Button icon={MailPlus} variant="secondary" size="sm" onClick={onRetry}>
          Send the invitation again
        </Button>
      </div>
    )
  }
  if (!result) {
    return null
  }
  return (
    <div className="space-y-2">
      <p className="text-xs text-ink">
        Invited to the app as <span className="font-bold">{result.invitation.email}</span>
        {result.handOff ? '.' : texted ? ', by email and by text.' : ', by email.'} They set their own password from the invitation.
      </p>
      <InvitationHandOffBanner result={result} onDismiss={onDismissHandOff} />
    </div>
  )
}
